import produce from 'immer';
import React from 'react';

import {ControlBarAction, ControlBarActionItems} from 'common/query-builder/query-entry-section/constants';
import {
  DraggableBehavior,
  useRawBehaviorsList,
} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/behaviors-list';
import type {RowProps} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import {getEmptyEventMetricEntryBehavior} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/utils';
import {MetricEntryBehavior} from 'common/query-builder/query-entry-section/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {useTranslation} from 'common/i18n/hooks';

import {getBehaviorEventIcon, toggleAnyOrder} from '../util';

/**
 * Component to render a behavior specifying a funnel step comparison
 */
export function EventComparisonRow(
  props: Omit<RowProps, `index`> & {
    metricIndex: number;
    disableAnyOrderMessage?: string;
    /** the index of the behavior containing the event comparison */
    behaviorIndex: number;
  },
) {
  /** EventComparisonRow consumes from a specified behavior from the metric entry */
  const entryBehavior = props.entry.behavior?.behaviors?.[props.behaviorIndex];
  if (!entryBehavior || !entryBehavior.behaviors) {
    throw Error(`EventComparisonRow rendered on an incompatible behavior`);
  }

  const {t} = useTranslation();
  const isStagingEventComparison = React.useRef(!entryBehavior.behaviors.at(-1)?.name);
  const {behaviors, handleUpdateBehavior, handleDuplicateBehavior, handleAddBehavior} = useRawBehaviorsList(
    entryBehavior.behaviors,
    (state, opts) => {
      const entry = produce(props.entry, (draft) => {
        draft.behavior!.behaviors![props.behaviorIndex].behaviors = state;
      });

      props.updateEntry(entry, opts);
    },
  );

  const handleAddEventComparison = () => {
    isStagingEventComparison.current = true;
    handleAddBehavior(getEmptyEventMetricEntryBehavior(), {requery: false});
  };

  const handleUpdateEventComparison = (index: number, updatedBehavior: MetricEntryBehavior) => {
    isStagingEventComparison.current = false;
    handleUpdateBehavior(index, updatedBehavior);
  };

  /**
   * Handles the deletion of an event-comparison behavior at the specified index.
   *
   * If removing the given event-comparison behavior leaves the parent behavior
   * with only one nested behavior left, we collapse this nested behaviors and
   * turn the parent behavior into a last remaining nested behavior.
   *
   * E.g. If you have behavior: { behaviors: [A,B] } and delete B, the behavior
   * will be updated to behavior: { B }.
   *
   * @param index Index of the behavior to update.
   */
  const handleDeleteEventComparison = (index: number, opts: {requery?: boolean} = {}) => {
    const updatedEntry = produce(props.entry, (entryDraft) => {
      const behavior = entryDraft.behavior!.behaviors![props.behaviorIndex];
      // Remove the event comparison event at the specified index.
      behavior.behaviors!.splice(index, 1);
      // If we only have one nested behavior left, we should flatten it.
      if (behavior.behaviors!.length === 1) {
        entryDraft.behavior!.behaviors![props.behaviorIndex] = {
          ...behavior.behaviors![0],
        };
      }
      return entryDraft;
    });
    props.updateEntry(updatedEntry, {requery: opts?.requery});
  };

  const handleCloseEventComparison = (index: number) => {
    // remove a staged entry if the events menu is closed
    if (isStagingEventComparison.current && !behaviors[index].name) {
      isStagingEventComparison.current = false;
      handleDeleteEventComparison(index, {requery: false});
    }
  };

  const rowIcon = getBehaviorEventIcon(entryBehavior);

  return (
    <div>
      {behaviors.map((subBehavior, index) => {
        return (
          <DraggableBehavior
            {...props}
            showAnyEvent
            index={index}
            indexOverride={props.behaviorIndex}
            /** prevents drag interactions inside step comparisons */
            disableDrag={index > 0}
            metricIndex={props.metricIndex}
            parentMetricIndex={props.parentMetricIndex}
            // props.behaviorIndex is the index of the behavior index in the parent metric
            behaviorIndex={[props.behaviorIndex, index]}
            /** prevents drag interactions between step comparisons */
            disableSortZones={index !== 0}
            isLast={index === behaviors.length - 1}
            additionalOverflowControls={[
              {
                ...(ControlBarActionItems as any)[ControlBarAction.AddGroupedEvent],
                onSelect: () => handleAddEventComparison(),
              },
              {
                ...(ControlBarActionItems as any)[ControlBarAction.ToggleAnyOrder],
                onSelect: () =>
                  toggleAnyOrder({
                    entry: props.entry,
                    behaviorIndex: props.behaviorIndex,
                    onUpdate: (entry) => props.updateEntry(entry),
                  }),
                ...(props.disableAnyOrderMessage
                  ? {isDisabled: true, tooltip: {text: props.disableAnyOrderMessage}}
                  : {}),
              },
            ]}
            behavior={{...subBehavior, name: subBehavior.name ?? t(`query-builder.metrics.add-step`)}}
            /** prevents behavioral or computed filters from showing up */
            otherFilterOptions={[]}
            // @todo(metrics) Figure out a way to uniquely identify each behavior in a metric
            // key={index}
            iconLabel={rowIcon ? null : String(props.behaviorIndex + 1)}
            onClose={() => handleCloseEventComparison(index)}
            onDelete={() => handleDeleteEventComparison(index)}
            onDuplicate={() => handleDuplicateBehavior(index)}
            onSelect={(behavior) => handleUpdateEventComparison(index, behavior)}
            // only open  last staged event
            openQueryBlock={index === behaviors.length - 1 && !behaviors.at(-1)?.name}
            prefixLabel={getAlphaLabelFromIndex(index) + ` `}
            rowIcon={rowIcon}
            suffixLabel={behaviors.length - 1 === index ? ` then` : ` or`}
          />
        );
      })}
    </div>
  );
}
